import {
    Box,
    Button,
    Card,
    CardBody,
    Modal,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Avatar,
    Heading,
    ModalCloseButton,
    Stack,
    useRadioGroup,
    PinInput,
    PinInputField,
    VStack,
    Alert,
    AlertDescription,
    AlertIcon
} from '@chakra-ui/react'
import { useState } from 'react'
import { useAlertService } from '@/shared/lib/useAlertService'
import { useChildByQrCode, useCheckoutChild } from '@/modules/teacher/hooks/useCheckInOut'
import { useChildCheckinHistory } from '@/modules/teacher/hooks/useChildCheckinHistory'
import { getFromLocalStorage } from '@/shared/utils/localStorageHelper'
import { BranchType } from '@/shared/api/hooks/useBranches'
import UserRadioCard from './UserRadioCard'
import CheckinHistoryList from '@/modules/teacher/components/CheckinHistoryList'
import { CHECKIN_CODE_LENGTH, CHECKIN_HISTORY_LIMIT } from '../constants'
import { CustomModalProps, User } from '../types'

const CheckOutModal = ({ qrCode, onClose, isOpen, session }: CustomModalProps) => {
    const alertService = useAlertService()

    const { loading, data } = useChildByQrCode(qrCode, {
        onError: (error) => {
            alertService.error(error.message, error.name)
            onClose()
        }
    })
    const {
        history,
        openCheckin,
        loading: historyLoading
    } = useChildCheckinHistory(qrCode, CHECKIN_HISTORY_LIMIT)

    const [checkedById, setCheckedById] = useState('')
    const [checkinCode, setCheckinCode] = useState('')
    const [checkOut, checkOutResult] = useCheckoutChild()
    const currentBranch = getFromLocalStorage<BranchType>('currentBranch')

    // The generator now issues 6-character codes, but records created before
    // that change still carry 5. Size the input to the code actually on the
    // open record so both eras can be checked out; fall back to the current
    // length when there is no open record to read.
    const codeLength = openCheckin?.checkinCode?.length || CHECKIN_CODE_LENGTH
    const isDisabled = checkinCode.length < codeLength

    // Derived rather than mirrored into state: it is a pure function of `data`.
    const groupData: User[] = data
        ? [
              ...data.getChildByQRCode.parents,
              ...(data.getChildByQRCode.alternativeContacts ?? [])
          ]
        : []

    const handleCheckinCode = (value: string) => {
        setCheckinCode(value.toUpperCase())
    }

    const submitChildCheck = async () => {
        let firstName, lastName
        if (data?.getChildByQRCode) {
            firstName = data.getChildByQRCode.firstName
            lastName = data.getChildByQRCode.lastName
        }
        // currentBranch is need in checkIn, checkOut else mutation give error
        if (!currentBranch) {
            alertService.error("Teacher's current branch is not set")
            return
        }

        try {
            await checkOut({
                variables: {
                    input: {
                        qrCode: qrCode,
                        checkOutBy: checkedById,
                        branch: currentBranch.value,
                        checkinCode: checkinCode,
                        session: session
                    }
                }
            })
            alertService.success(`Checked out ${firstName} ${lastName}`, 'Success')
            onClose()
        } catch (error: any) {
            alertService.error(error.message, error.name)
        }
    }

    // set up radio cards for check-in/out parents
    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'checkedById',
        defaultValue: '',
        onChange: setCheckedById
    })

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            motionPreset="slideInBottom"
            scrollBehavior="outside"
            blockScrollOnMount={false}
            size={{ base: 'xs', md: 'md' }}
        >
            <ModalOverlay backdropFilter="blur(8px)" />
            {loading ? (
                <Spinner size="lg" />
            ) : (
                <ModalContent bg="white">
                    <ModalHeader fontWeight="normal">Checkout Process</ModalHeader>
                    <ModalCloseButton />
                    {!!data && (
                        <Card>
                            <CardBody>
                                <VStack my={2} gap="4">
                                    <Avatar
                                        borderWidth="1px"
                                        size="2xl"
                                        borderColor="gray.100"
                                        name={`${data.getChildByQRCode.firstName} ${data.getChildByQRCode.lastName}`}
                                        src={data.getChildByQRCode.picture}
                                    />
                                    <Text>{`${data.getChildByQRCode.firstName} ${data.getChildByQRCode.lastName}`}</Text>
                                </VStack>

                                {data.getChildByQRCode && (
                                    <Box>
                                        {!historyLoading && !openCheckin ? (
                                            <Alert
                                                status="warning"
                                                borderRadius="md"
                                                fontSize="sm"
                                                mb={2}
                                            >
                                                <AlertIcon />
                                                <AlertDescription>
                                                    This child has no open check-in, so
                                                    there is nothing to check out. They
                                                    may need checking in instead.
                                                </AlertDescription>
                                            </Alert>
                                        ) : null}

                                        <Heading py={4} size="md">
                                            Who is taking out this child?
                                        </Heading>
                                        <Stack
                                            marginTop={2}
                                            marginBottom={2}
                                            {...getRootProps()}
                                        >
                                            {groupData.map((parent) => {
                                                return (
                                                    <UserRadioCard
                                                        key={parent._id}
                                                        parent={parent}
                                                        {...getRadioProps({
                                                            value: parent._id
                                                        })}
                                                    />
                                                )
                                            })}
                                        </Stack>

                                        <CheckinHistoryList
                                            history={history}
                                            loading={historyLoading}
                                        />

                                        {/* Requires code when you are checking out */}
                                        <Stack mt="8">
                                            <Heading fontSize="md">Checkin Code</Heading>
                                            <Text color="gray">
                                                The code created when the child was
                                                checked-in
                                            </Text>
                                            <Stack direction="row" my="2">
                                                {/* One field per character of the real
                                                    code. This row was hardcoded to 5
                                                    while the backend had moved to 6, so
                                                    every recent checkout failed as
                                                    "Invalid check-in code". */}
                                                <PinInput
                                                    type="alphanumeric"
                                                    size="lg"
                                                    value={checkinCode}
                                                    onChange={handleCheckinCode}
                                                >
                                                    {Array.from({
                                                        length: codeLength
                                                    }).map((_, index) => (
                                                        <PinInputField key={index} />
                                                    ))}
                                                </PinInput>
                                            </Stack>
                                            <Text fontSize="sm">
                                                *Codes are not case sensitive
                                            </Text>
                                        </Stack>

                                        <Box pt={6}>
                                            <Button
                                                width="full"
                                                isDisabled={!checkedById || isDisabled}
                                                isLoading={checkOutResult.loading}
                                                onClick={submitChildCheck}
                                            >
                                                Check out child
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </ModalContent>
            )}
        </Modal>
    )
}

export default CheckOutModal
